import React, { useState, useEffect, useCallback, useMemo } from 'react';
import axios from 'axios';
import { useAuth } from '../../context/AuthContext';
import { useNavigate } from 'react-router-dom';
import { ChevronLeft, ChevronRight, Calendar } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

const ESTADO_COLORS = {
  EN_VALIDACION: '#3b82f6',
  PEND_APROBACION: '#a855f7',
  APROBADA: '#10b981',
  RECHAZADA: '#ef4444',
};
const ESTADO_LABELS = { EN_VALIDACION: 'En Validacion', PEND_APROBACION: 'Pend. Aprobacion', APROBADA: 'Aprobada', RECHAZADA: 'Rechazada' };
const RIESGO_BORDER = { BAJO: '#22c55e', MEDIO: '#eab308', ALTO: '#f97316', CRITICO: '#ef4444' };

function getMonday(d) {
  const dt = new Date(d);
  dt.setDate(dt.getDate() - dt.getDay() + 1);
  dt.setHours(0, 0, 0, 0);
  return dt;
}

export default function GanttActividades() {
  const { token } = useAuth();
  const navigate = useNavigate();
  const headers = { Authorization: `Bearer ${token}` };
  const [actividades, setActividades] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filtroEstado, setFiltroEstado] = useState('');
  const [viewMode, setViewMode] = useState('semana'); // hoy, semana, mes, custom
  const [customFrom, setCustomFrom] = useState('');
  const [customTo, setCustomTo] = useState('');
  const [weekStart, setWeekStart] = useState(() => getMonday(new Date()));

  const fetchActividades = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (filtroEstado) params.set('estado', filtroEstado);
      const res = await axios.get(`${API}/actividades-operacionales?${params}`, { headers });
      setActividades(res.data);
    } catch { }
    setLoading(false);
  }, [token, filtroEstado]);

  useEffect(() => { fetchActividades(); }, [fetchActividades]);

  // Compute date range based on viewMode
  const { rangeStart, rangeEnd, rangeDays } = useMemo(() => {
    const today = new Date(); today.setHours(0, 0, 0, 0);
    let start, end;

    if (viewMode === 'hoy') {
      start = new Date(today);
      end = new Date(today);
    } else if (viewMode === 'semana') {
      start = new Date(weekStart);
      end = new Date(weekStart);
      end.setDate(end.getDate() + 6);
    } else if (viewMode === 'mes') {
      start = new Date(weekStart.getFullYear(), weekStart.getMonth(), 1);
      end = new Date(weekStart.getFullYear(), weekStart.getMonth() + 1, 0);
    } else if (viewMode === 'custom' && customFrom && customTo) {
      start = new Date(customFrom + 'T00:00:00');
      end = new Date(customTo + 'T00:00:00');
    } else {
      start = new Date(weekStart);
      end = new Date(weekStart); end.setDate(end.getDate() + 6);
    }

    const days = [];
    const d = new Date(start);
    while (d <= end) {
      days.push(new Date(d));
      d.setDate(d.getDate() + 1);
    }
    return { rangeStart: start, rangeEnd: end, rangeDays: days };
  }, [viewMode, weekStart, customFrom, customTo]);

  // Navigation
  const navigate_ = (dir) => {
    const offset = dir === 'prev' ? -1 : 1;
    if (viewMode === 'hoy') {
      const d = new Date(weekStart); d.setDate(d.getDate() + offset); setWeekStart(d);
    } else if (viewMode === 'semana') {
      const d = new Date(weekStart); d.setDate(d.getDate() + (offset * 7)); setWeekStart(d);
    } else if (viewMode === 'mes') {
      const d = new Date(weekStart); d.setMonth(d.getMonth() + offset); setWeekStart(d);
    }
  };

  const goToday = () => {
    const today = new Date(); today.setHours(0, 0, 0, 0);
    if (viewMode === 'semana') setWeekStart(getMonday(today));
    else setWeekStart(today);
  };

  const formatDate = (d) => d.toLocaleDateString('es-CL', { day: '2-digit', month: 'short' });
  const formatRangeLabel = () => {
    if (viewMode === 'hoy') return `Hoy - ${formatDate(rangeStart)}`;
    if (viewMode === 'semana') return `${formatDate(rangeStart)} - ${formatDate(rangeEnd)}`;
    if (viewMode === 'mes') return rangeStart.toLocaleDateString('es-CL', { month: 'long', year: 'numeric' });
    if (viewMode === 'custom' && customFrom && customTo) return `${formatDate(rangeStart)} - ${formatDate(rangeEnd)}`;
    return '';
  };

  const dayNames = ['Dom', 'Lun', 'Mar', 'Mie', 'Jue', 'Vie', 'Sab'];

  const HOUR_START = 5;
  const HOUR_END = 20;
  const TOTAL_HOURS = HOUR_END - HOUR_START;

  // Filter activities in range
  const rangeActivities = useMemo(() => {
    return actividades.filter(a => {
      const actDate = new Date(a.fecha_planificada + 'T12:00:00');
      return actDate >= rangeStart && actDate <= rangeEnd;
    }).sort((a, b) => a.fecha_planificada.localeCompare(b.fecha_planificada) || (a.hora_inicio || '').localeCompare(b.hora_inicio || ''));
  }, [actividades, rangeStart, rangeEnd]);

  // Group by day
  const dayActivities = useMemo(() => {
    const map = {};
    rangeDays.forEach(d => { map[d.toISOString().split('T')[0]] = []; });
    rangeActivities.forEach(a => { if (map[a.fecha_planificada]) map[a.fecha_planificada].push(a); });
    return map;
  }, [rangeActivities, rangeDays]);

  const getBarStyle = (act) => {
    const start = act.hora_inicio ? parseInt(act.hora_inicio.split(':')[0]) + parseInt(act.hora_inicio.split(':')[1]) / 60 : HOUR_START;
    const end = act.hora_termino ? parseInt(act.hora_termino.split(':')[0]) + parseInt(act.hora_termino.split(':')[1]) / 60 : start + (act.duracion_horas || 8);
    const clampedStart = Math.max(start, HOUR_START);
    const clampedEnd = Math.min(end, HOUR_END);
    const left = ((clampedStart - HOUR_START) / TOTAL_HOURS) * 100;
    const width = ((clampedEnd - clampedStart) / TOTAL_HOURS) * 100;
    return { left: `${left}%`, width: `${Math.max(width, 2)}%` };
  };

  // Compact mode for month view (no hour grid, just bars proportional)
  const isCompact = viewMode === 'mes' || (viewMode === 'custom' && rangeDays.length > 14);

  return (
    <div className="space-y-4" data-testid="gantt-actividades">
      {/* Controls */}
      <div className="flex items-center justify-between flex-wrap gap-3">
        {/* View mode tabs */}
        <div className="flex gap-1 bg-slate-100 p-1 rounded-lg" data-testid="gantt-view-tabs">
          {[
            { key: 'hoy', label: 'Hoy' },
            { key: 'semana', label: 'Semana' },
            { key: 'mes', label: 'Mes' },
            { key: 'custom', label: 'Rango' },
          ].map(t => (
            <button key={t.key} onClick={() => {
              setViewMode(t.key);
              if (t.key === 'hoy') { const d = new Date(); d.setHours(0,0,0,0); setWeekStart(d); }
              if (t.key === 'semana') setWeekStart(getMonday(weekStart));
            }}
              className={`px-3 py-1.5 rounded-md text-xs font-medium transition-colors ${viewMode === t.key ? 'bg-[#1B919B] text-white shadow-sm' : 'text-slate-500 hover:text-slate-700'}`}
              data-testid={`gantt-view-${t.key}`}>
              {t.label}
            </button>
          ))}
        </div>

        <div className="flex items-center gap-2">
          <select value={filtroEstado} onChange={e => setFiltroEstado(e.target.value)}
            className="px-3 py-1.5 border border-slate-200 rounded-lg text-xs" data-testid="gantt-filter-estado">
            <option value="">Todos los estados</option>
            {Object.entries(ESTADO_LABELS).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
          </select>
        </div>
      </div>

      {/* Custom date pickers */}
      {viewMode === 'custom' && (
        <div className="flex items-center gap-3 bg-white rounded-lg border border-slate-200 p-3">
          <Calendar className="w-4 h-4 text-slate-400" />
          <div className="flex items-center gap-2">
            <label className="text-xs text-slate-400">Desde</label>
            <input type="date" value={customFrom} onChange={e => setCustomFrom(e.target.value)}
              className="px-2 py-1 border border-slate-200 rounded text-xs" data-testid="gantt-date-from" />
          </div>
          <div className="flex items-center gap-2">
            <label className="text-xs text-slate-400">Hasta</label>
            <input type="date" value={customTo} onChange={e => setCustomTo(e.target.value)}
              className="px-2 py-1 border border-slate-200 rounded text-xs" data-testid="gantt-date-to" />
          </div>
          <span className="text-xs text-slate-400">{rangeDays.length} dia(s)</span>
        </div>
      )}

      {/* Navigation */}
      {viewMode !== 'custom' && (
        <div className="bg-white rounded-xl border border-slate-200 p-3 flex items-center justify-between">
          <button onClick={() => navigate_('prev')} className="p-2 hover:bg-slate-100 rounded-lg" data-testid="gantt-prev"><ChevronLeft className="w-5 h-5 text-slate-500" /></button>
          <div className="flex items-center gap-3">
            <span className="text-sm font-semibold text-slate-800">{formatRangeLabel()}</span>
            <button onClick={goToday} className="text-xs px-2.5 py-1 bg-[#1B919B]/10 text-[#1B919B] rounded-full font-medium hover:bg-[#1B919B]/20" data-testid="gantt-today">Hoy</button>
          </div>
          <button onClick={() => navigate_('next')} className="p-2 hover:bg-slate-100 rounded-lg" data-testid="gantt-next"><ChevronRight className="w-5 h-5 text-slate-500" /></button>
        </div>
      )}

      {/* Leyenda */}
      <div className="flex flex-wrap gap-3">
        {Object.entries(ESTADO_COLORS).map(([k, c]) => (
          <div key={k} className="flex items-center gap-1.5">
            <div className="w-3 h-3 rounded-sm" style={{ backgroundColor: c }} />
            <span className="text-xs text-slate-500">{ESTADO_LABELS[k]}</span>
          </div>
        ))}
        <div className="border-l border-slate-200 pl-3 flex items-center gap-1.5">
          <div className="w-3 h-3 rounded-sm border-2" style={{ borderColor: '#ef4444' }} />
          <span className="text-xs text-slate-500">Riesgo Critico</span>
        </div>
      </div>

      {/* Gantt Chart */}
      <div className="bg-white rounded-xl border border-slate-200 overflow-hidden">
        {loading ? (
          <div className="flex items-center justify-center py-20"><div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#1B919B]" /></div>
        ) : (
          <div className="overflow-x-auto">
            <div className={isCompact ? 'min-w-[600px]' : 'min-w-[900px]'}>
              {/* Header */}
              {!isCompact && (
                <div className="flex border-b border-slate-200">
                  <div className="w-[200px] shrink-0 px-3 py-2 bg-slate-50 border-r border-slate-200">
                    <span className="text-xs font-semibold text-slate-500">ACTIVIDAD</span>
                  </div>
                  <div className="flex-1 relative">
                    <div className="flex">
                      {Array.from({ length: TOTAL_HOURS }, (_, i) => (
                        <div key={i} className="flex-1 text-center py-2 border-r border-slate-100 last:border-0 bg-slate-50">
                          <span className="text-[10px] text-slate-400">{String(HOUR_START + i).padStart(2, '0')}:00</span>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              )}

              {/* Compact header for month */}
              {isCompact && (
                <div className="flex border-b border-slate-200 bg-slate-50 px-3 py-2">
                  <span className="text-xs font-semibold text-slate-500">DIA</span>
                  <span className="ml-auto text-xs font-semibold text-slate-500">ACTIVIDADES</span>
                </div>
              )}

              {/* Days */}
              {rangeDays.map((day) => {
                const dateKey = day.toISOString().split('T')[0];
                const acts = dayActivities[dateKey] || [];
                const isToday = new Date().toISOString().split('T')[0] === dateKey;
                const isWeekend = day.getDay() === 0 || day.getDay() === 6;

                return (
                  <div key={dateKey}>
                    {/* Day header */}
                    <div className={`flex items-center px-3 py-1.5 border-b border-slate-100 ${isToday ? 'bg-[#1B919B]/5' : isWeekend ? 'bg-slate-50/50' : ''}`}>
                      <span className={`text-xs font-semibold ${isToday ? 'text-[#1B919B]' : 'text-slate-500'}`}>
                        {dayNames[day.getDay()]} {day.getDate()}/{day.getMonth() + 1}
                      </span>
                      {isToday && <span className="ml-2 text-[10px] bg-[#1B919B] text-white px-1.5 py-0.5 rounded-full">HOY</span>}
                      <span className="ml-auto text-[10px] text-slate-400">{acts.length} actividad(es)</span>
                    </div>

                    {/* Activity rows */}
                    {acts.length === 0 ? (
                      !isCompact && (
                        <div className={`flex border-b border-slate-50 ${isWeekend ? 'bg-slate-50/30' : ''}`}>
                          <div className="w-[200px] shrink-0 border-r border-slate-100" />
                          <div className="flex-1 h-6" />
                        </div>
                      )
                    ) : isCompact ? (
                      /* Compact bars for month view */
                      acts.map(act => {
                        const color = ESTADO_COLORS[act.estado] || '#94a3b8';
                        const borderColor = RIESGO_BORDER[act.nivel_riesgo] || 'transparent';
                        const hasCrit = act.nivel_riesgo === 'CRITICO' || act.nivel_riesgo === 'ALTO';
                        return (
                          <div key={act.id} className="flex items-center px-3 py-1 border-b border-slate-50 hover:bg-slate-50/50 cursor-pointer group"
                            onClick={() => navigate(`/planificador/actividades/${act.id}`)} data-testid={`gantt-bar-${act.id}`}>
                            <div className="w-3 h-3 rounded-sm mr-2 flex-shrink-0" style={{ backgroundColor: color, border: hasCrit ? `2px solid ${borderColor}` : 'none' }} />
                            <span className="text-[10px] font-mono text-slate-400 mr-2">{act.hora_inicio || ''}</span>
                            <span className="text-xs text-slate-700 truncate flex-1 group-hover:text-[#1B919B]">{act.titulo}</span>
                            <span className="text-[10px] text-slate-400 ml-2">{act.duracion_horas}h</span>
                          </div>
                        );
                      })
                    ) : (
                      /* Full bars for day/week view */
                      acts.map(act => {
                        const barStyle = getBarStyle(act);
                        const color = ESTADO_COLORS[act.estado] || '#94a3b8';
                        const borderColor = RIESGO_BORDER[act.nivel_riesgo] || 'transparent';
                        const hasCritBorder = act.nivel_riesgo === 'CRITICO' || act.nivel_riesgo === 'ALTO';
                        return (
                          <div key={act.id} className="flex border-b border-slate-50 hover:bg-slate-50/50 transition-colors group">
                            <div className="w-[200px] shrink-0 px-3 py-2 border-r border-slate-100 flex flex-col justify-center cursor-pointer"
                              onClick={() => navigate(`/planificador/actividades/${act.id}`)}>
                              <span className="text-xs font-mono text-slate-400">{act.codigo}</span>
                              <span className="text-xs font-medium text-slate-700 truncate group-hover:text-[#1B919B]">{act.titulo}</span>
                            </div>
                            <div className="flex-1 relative py-1.5">
                              {Array.from({ length: TOTAL_HOURS }, (_, i) => (
                                <div key={i} className="absolute top-0 bottom-0 border-r border-slate-50" style={{ left: `${(i / TOTAL_HOURS) * 100}%` }} />
                              ))}
                              <div
                                className="absolute top-1.5 bottom-1.5 rounded cursor-pointer transition-opacity hover:opacity-90"
                                style={{
                                  ...barStyle,
                                  backgroundColor: color,
                                  border: hasCritBorder ? `2px solid ${borderColor}` : 'none',
                                  minWidth: '20px',
                                }}
                                onClick={() => navigate(`/planificador/actividades/${act.id}`)}
                                title={`${act.titulo}\n${act.hora_inicio || '?'} - ${act.hora_termino || '?'} (${act.duracion_horas}h)\n${act.estado} | Riesgo: ${act.nivel_riesgo}`}
                                data-testid={`gantt-bar-${act.id}`}
                              >
                                <span className="text-[9px] text-white font-medium px-1.5 truncate block leading-6 whitespace-nowrap overflow-hidden">
                                  {act.hora_inicio} {act.titulo}
                                </span>
                              </div>
                            </div>
                          </div>
                        );
                      })
                    )}
                  </div>
                );
              })}
            </div>
          </div>
        )}
      </div>

      {/* Summary */}
      <div className="flex items-center gap-4 text-xs text-slate-400">
        <span>{rangeActivities.length} actividad(es) en el periodo</span>
        <span>|</span>
        <span>{rangeActivities.filter(a => a.estado === 'APROBADA').length} aprobadas</span>
        <span>{rangeActivities.filter(a => a.estado === 'PEND_APROBACION').length} pendientes</span>
      </div>
    </div>
  );
}
